import * as React from 'react';
import Chart from 'chart.js';
import { themeDark } from 'styles/theme';
import { BarChartProps, MAX_BAR_GRAPH_DAYS, ValueType } from './props';
import {
  BASE_CHART_OPTIONS,
  BASE_X_TICK_STYLE,
  buildYAxisConfig,
  buildNetDataset,
  buildTotalDatasets,
  getShortLabel,
  getChartFromTooltipContext,
} from './chartConfig';
import {
  getOrCreateTooltipEl,
  removeTooltipEl,
  positionTooltip,
  tooltipRow,
  wrapTooltipTable,
  getTooltipMaxWidth,
} from './tooltipHelpers';

let nextTrendsInstanceId = 0;

interface ProcessedTradeData {
  unusualCalls: number;
  normalCalls: number;
  totalCalls: number;
  unusualPuts: number;
  normalPuts: number;
  totalPuts: number;
  bullishValue: number;
  bearishValue: number;
  netValue: number;
  neutralValue: number;
}

const BarChartTrends: React.FC<React.PropsWithChildren<BarChartProps>> = ({
  tradesByDate: originalTradesByDate,
  label,
  prefix,
  formatter,
  dataType,
  valueType = ValueType.Net,
}) => {
  const chartWrapperRef = React.useRef<HTMLCanvasElement>(null);
  const chartRef = React.useRef<Chart | undefined>(undefined);
  const instanceSuffix = React.useRef(++nextTrendsInstanceId).current;
  const TOOLTIP_ID = `chartjs-tooltip-trends-${instanceSuffix}`;
  const CARET_ID = `tooltip-caret-${instanceSuffix}`;

  const tradesByDate = React.useMemo(
    () => [...originalTradesByDate].slice(0, MAX_BAR_GRAPH_DAYS).reverse(),
    [originalTradesByDate],
  );

  const processedData = React.useMemo((): ProcessedTradeData[] => {
    return tradesByDate.map((t) => {
      const isVolume = dataType === 'volume';

      const unusualCalls = isVolume ? t.unusualCallVolume : t.unusualCallPremium;
      const unusualPuts = isVolume ? t.unusualPutVolume : t.unusualPutPremium;
      const totalCalls = isVolume ? t.callVolume : t.callPremium;
      const totalPuts = isVolume ? t.putVolume : t.putPremium;
      const totalBullish = isVolume
        ? (t.bullishVolume ?? totalCalls)
        : (t.bullishPremium ?? totalCalls);
      const totalBearish = isVolume
        ? (t.bearishVolume ?? totalPuts)
        : (t.bearishPremium ?? totalPuts);
      const neutralValue = isVolume ? t.neutralVolume || 0 : t.neutralPremium || 0;
      const netValue = totalBullish - totalBearish;

      return {
        unusualCalls,
        normalCalls: Math.max(totalCalls - unusualCalls, 0),
        totalCalls,
        unusualPuts,
        normalPuts: Math.max(totalPuts - unusualPuts, 0),
        totalPuts,
        bullishValue: totalBullish,
        bearishValue: totalBearish,
        netValue,
        neutralValue,
      };
    });
  }, [tradesByDate, dataType]);

  const labels = React.useMemo(() => {
    return tradesByDate.map((t) => {
      const splitDate = t.date.split('-');
      const month = splitDate[1];
      const day = splitDate[2];
      return `${parseInt(month, 10)}/${parseInt(day, 10)}`;
    });
  }, [tradesByDate]);

  // Cleanup tooltip DOM element on unmount
  React.useEffect(() => {
    return () => {
      removeTooltipEl(TOOLTIP_ID);
    };
  }, []);

  React.useEffect(() => {
    if (chartRef.current) {
      chartRef.current.destroy();
      chartRef.current = undefined;
    }

    const ctx = chartWrapperRef.current?.getContext('2d');
    if (!ctx || processedData.length === 0) return;

    const currentProcessedData = processedData;
    const isNetMode = valueType === ValueType.Net;

    const yAxisConfig = buildYAxisConfig({
      isNetMode,
      yAxisLabel: label,
      prefix,
      formatter,
      netValues: isNetMode ? currentProcessedData.map((t) => t.netValue) : undefined,
    });

    const shortLabel = getShortLabel(label);
    const datasets = isNetMode
      ? buildNetDataset(currentProcessedData, (t) => t.netValue, shortLabel)
      : buildTotalDatasets(currentProcessedData, {
          unusualCalls: (t) => t.unusualCalls,
          normalCalls: (t) => t.normalCalls,
          unusualPuts: (t) => t.unusualPuts,
          normalPuts: (t) => t.normalPuts,
        });

    chartRef.current = new Chart(ctx, {
      type: 'bar',
      data: {
        datasets,
        labels: labels,
      },
      options: {
        ...BASE_CHART_OPTIONS,
        tooltips: {
          enabled: false,
          custom: function (tooltipModel) {
            const tooltipEl = getOrCreateTooltipEl(TOOLTIP_ID);

            if (tooltipModel.opacity === 0) {
              tooltipEl.style.opacity = '0';
              return;
            }

            const dataPoint = tooltipModel.dataPoints?.[0];
            if (!dataPoint) return;

            const index = dataPoint.index as number;
            const datasetIndex = dataPoint.datasetIndex as number;
            const tradeData = currentProcessedData[index];
            const chart = getChartFromTooltipContext(this);
            const dateLabel = chart.data.labels[index];

            if (!tradeData) return;

            const position = chart.canvas.getBoundingClientRect();
            const caretX = position.left + tooltipModel.caretX;
            const caretY = position.top + tooltipModel.caretY;

            if (isNetMode) {
              const netValue = tradeData.netValue;
              const totalBullish = tradeData.bullishValue || 0;
              const totalBearish = tradeData.bearishValue || 0;
              const neutralValue = tradeData.neutralValue || 0;
              const total = totalBullish + totalBearish;
              const callPercent = total > 0 ? ((totalBullish / total) * 100).toFixed(0) : '0';
              const putPercent = total > 0 ? ((totalBearish / total) * 100).toFixed(0) : '0';
              const isPositive = netValue >= 0;
              const netSign = isPositive ? '+' : '-';
              const fullDate = tradesByDate[index]?.date || dateLabel;

              const rows =
                tooltipRow('Date', fullDate) +
                tooltipRow(
                  `Total Bullish ${label}`,
                  `${prefix}${formatter(totalBullish)}`,
                  themeDark.color.graphCall,
                ) +
                tooltipRow(
                  `Total Bearish ${label}`,
                  `${prefix}${formatter(totalBearish)}`,
                  themeDark.color.graphPut,
                ) +
                tooltipRow(
                  `Net ${label}`,
                  `${netSign}${prefix}${formatter(Math.abs(netValue))}`,
                  isPositive ? themeDark.color.graphCall : themeDark.color.graphPut,
                ) +
                (neutralValue
                  ? tooltipRow(
                      'Neutral',
                      `${prefix}${formatter(neutralValue)}`,
                      themeDark.color.textLight,
                    )
                  : '') +
                tooltipRow('% Bullish', `${callPercent}%`, undefined, false) +
                tooltipRow('% Bearish', `${putPercent}%`, undefined, false);

              tooltipEl.innerHTML = wrapTooltipTable(rows, CARET_ID);
            } else {
              const isCall = datasetIndex <= 1;
              const total = isCall ? tradeData.totalCalls : tradeData.totalPuts;
              const unusual = isCall ? tradeData.unusualCalls : tradeData.unusualPuts;
              const normal = isCall ? tradeData.normalCalls : tradeData.normalPuts;
              const unusualPercent = total > 0 ? ((unusual / total) * 100).toFixed(1) : '0.0';
              const unusualColor = isCall
                ? themeDark.color.graphUnusualCall
                : themeDark.color.graphUnusualPut;
              const normalColor = isCall ? themeDark.color.graphCall : themeDark.color.graphPut;
              const maxWidth = getTooltipMaxWidth();

              tooltipEl.innerHTML = `
                <div class="bg-black/80 rounded-md text-white text-xs py-1.5 px-2.5 relative" style="max-width: ${maxWidth};">
                  <div class="mb-1.5">${dateLabel} - ${isCall ? 'Calls' : 'Puts'}</div>
                  <div class="flex items-center mb-1">
                    <span class="w-2.5 h-2.5 mr-2 rounded-sm shrink-0" style="background: linear-gradient(to right, ${unusualColor} 50%, ${normalColor} 50%);"></span>
                    <span>Total: ${prefix}${formatter(total)}</span>
                  </div>
                  <div class="flex items-center mb-1">
                    <span class="w-2.5 h-2.5 mr-2 rounded-sm shrink-0" style="background: ${unusualColor};"></span>
                    <span>Unusual: ${prefix}${formatter(unusual)} (${unusualPercent}%)</span>
                  </div>
                  <div class="flex items-center">
                    <span class="w-2.5 h-2.5 mr-2 rounded-sm shrink-0" style="background: ${normalColor};"></span>
                    <span>Normal: ${prefix}${formatter(normal)}</span>
                  </div>
                  <div id="${CARET_ID}"></div>
                </div>
              `;
            }

            positionTooltip(tooltipEl, caretX, caretY, CARET_ID);
          },
        },
        scales: {
          yAxes: [yAxisConfig],
          xAxes: [
            {
              stacked: !isNetMode,
              ticks: {
                ...BASE_X_TICK_STYLE,
                fontSize: 11,
              },
            },
          ],
        },
      },
    });

    return () => {
      if (chartRef.current) {
        chartRef.current.destroy();
        chartRef.current = undefined;
      }
    };
  }, [processedData, labels, label, prefix, formatter, valueType]);

  return (
    <div style={{ maxHeight: '100%', height: '100%', width: '100%' }}>
      <canvas ref={chartWrapperRef} style={{ height: '100%', width: '100%' }} />
    </div>
  );
};

export default BarChartTrends;
